// One hosted app: health, components, metrics, deployments, logs and what the console did to it.
import React, { useEffect, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { ExternalLink, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { useBreadcrumbs } from '@/client/hooks/use-breadcrumbs';
import { useHasPermission } from '@/client/hooks/useHasPermission';
import {
  useCloudAppQuery,
  useCloudLogsQuery,
  useSetEnvMutation,
} from '@/client/api/cloud/cloud.query';
import {
  AppComponent,
  CloudApp,
  EnvName,
  EnvWrite,
  LogType,
  MetricWindow,
} from '@/client/types/cloud';
import {
  AccessDenied,
  Action,
  ActionDialog,
  AuditCard,
  FetchingChip,
  FormDialog,
  LoadingNote,
  CLOUD_BASE,
  Card,
  EmptyRow,
  EnvBadge,
  ErrorNotice,
  Field,
  HealthPill,
  HealthReasons,
  INPUT_CLASS,
  MetricChart,
  PageShell,
  PhaseBadge,
  Tabs,
  Th,
  UsagePct,
  deploymentCause,
  fmtAgo,
  fmtDur,
  fmtTime,
  isDeploying,
} from './cloudUi';

const METRIC_WINDOWS: readonly MetricWindow[] = ['1h', '6h', '24h', '7d'];
const SPAN_HOURS: Record<MetricWindow, number> = { '1h': 1, '6h': 6, '24h': 24, '7d': 168 };
const LOG_TYPES: readonly LogType[] = ['run', 'build', 'deploy'];

const ComponentRow: React.FC<{
  c: AppComponent;
  canOperate: boolean;
  canManage: boolean;
  onAction: (a: Action) => void;
  onEditEnv: () => void;
}> = ({ c, canOperate, canManage, onAction, onEditEnv }) => {
  const current = c.instance_count ?? 1;
  const [count, setCount] = useState(current);
  const [showEnv, setShowEnv] = useState(false);
  const scalable = (c.type === 'service' || c.type === 'worker') && !c.autoscaling;
  return (
    <>
      <tr className="border-b border-gray-50 align-top last:border-0">
        <td className="px-4 py-2">
          <span className="font-medium text-gray-900">{c.name}</span>
          <p className="text-xs text-gray-500">{c.type.replace('_', ' ')}</p>
        </td>
        <td className="px-4 py-2 text-gray-600">{c.size ?? '—'}</td>
        <td className="px-4 py-2 text-gray-700">
          {c.autoscaling ? (
            'autoscaled'
          ) : canManage && scalable ? (
            <span className="inline-flex items-center gap-2">
              <select
                value={count}
                onChange={(e) => setCount(Number(e.target.value))}
                aria-label={`Instances of ${c.name}`}
                className={`${INPUT_CLASS} h-8`}
              >
                {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
              {count !== current && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() =>
                    onAction({ kind: 'scale', component: c.name, from: current, to: count })
                  }
                >
                  Apply
                </Button>
              )}
            </span>
          ) : (
            (c.instance_count ?? '—')
          )}
        </td>
        <td className="px-4 py-2 text-gray-600">
          {c.repo ? `${c.repo}${c.branch ? ` @ ${c.branch}` : ''}` : '—'}
          {c.deploy_on_push === false && <p className="text-xs text-gray-400">manual deploys</p>}
        </td>
        <td className="px-4 py-2">
          {c.env.length > 0 && (
            <button
              type="button"
              className="text-xs text-blue-600 hover:underline"
              onClick={() => setShowEnv(!showEnv)}
            >
              {showEnv ? 'Hide' : `${c.env.length} variables`}
            </button>
          )}
          {canManage && (
            <button
              type="button"
              className="ml-3 text-xs text-blue-600 hover:underline"
              onClick={onEditEnv}
            >
              Edit
            </button>
          )}
        </td>
        <td className="px-4 py-2 text-right">
          {canOperate && c.type !== 'static_site' && c.type !== 'job' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onAction({ kind: 'restart', component: c.name })}
            >
              Restart
            </Button>
          )}
        </td>
      </tr>
      {showEnv && (
        <tr className="border-b border-gray-50">
          <td colSpan={6} className="px-4 pb-3">
            <EnvNames env={c.env} />
          </td>
        </tr>
      )}
    </>
  );
};

/**
 * Set, replace or remove variables on the app or on one component. A value can
 * be written but never read back, so replacing one means typing it again.
 */
const EnvDialog: React.FC<{
  app: CloudApp;
  target: string;
  env: EnvName[];
  onClose: () => void;
}> = ({ app, target, env, onClose }) => {
  const [rows, setRows] = useState<EnvWrite[]>([{ key: '', value: '', secret: true }]);
  const [removing, setRemoving] = useState<string[]>([]);
  const save = useSetEnvMutation();
  const filled = rows.filter((r) => r.key.trim());
  const update = (i: number, patch: Partial<EnvWrite>) =>
    setRows((all) => all.map((r, at) => (at === i ? { ...r, ...patch } : r)));
  const existing = new Set(env.map((e) => e.key));
  return (
    <FormDialog
      title={`Variables of ${target === 'app' ? app.name : target}`}
      description="Saving starts a deployment of the whole app. Values are written, never shown: to change one, type its name and its new value."
      confirmLabel="Save and deploy"
      destructive
      confirmText={app.name}
      valid={filled.length > 0 || removing.length > 0}
      busy={save.isPending}
      error={save.error}
      onClose={onClose}
      onConfirm={(reason) =>
        save.mutate(
          {
            appId: app.id,
            target,
            set: filled.map((r) => ({ ...r, key: r.key.trim() })),
            unset: removing,
            reason,
          },
          { onSuccess: onClose },
        )
      }
    >
      {env.length > 0 && (
        <div>
          <p className="text-sm text-gray-700">Set now. Tick one to remove it.</p>
          <div className="mt-1 flex flex-wrap gap-1.5">
            {env.map((e) => {
              const gone = removing.includes(e.key);
              return (
                <label
                  key={e.key}
                  className={`inline-flex cursor-pointer items-center gap-1.5 rounded px-1.5 py-0.5 font-mono text-[11px] ${
                    gone ? 'bg-red-50 text-red-700 line-through' : 'bg-gray-100 text-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={gone}
                    onChange={() =>
                      setRemoving((all) =>
                        gone ? all.filter((k) => k !== e.key) : [...all, e.key],
                      )
                    }
                  />
                  {e.key}
                </label>
              );
            })}
          </div>
        </div>
      )}
      <div>
        <p className="text-sm text-gray-700">Add, or replace by name</p>
        <div className="mt-1 space-y-2">
          {rows.map((r, i) => (
            // Rows are only ever appended, so the position is a stable key.
            // eslint-disable-next-line react/no-array-index-key
            <div key={i} className="grid grid-cols-[1fr_1.4fr_auto] items-center gap-2">
              <input
                value={r.key}
                onChange={(e) => update(i, { key: e.target.value })}
                placeholder="NAME"
                aria-label="Variable name"
                className={`${INPUT_CLASS} w-full font-mono`}
              />
              <input
                value={r.value}
                onChange={(e) => update(i, { value: e.target.value })}
                placeholder="value"
                aria-label="Variable value"
                type={r.secret ? 'password' : 'text'}
                autoComplete="off"
                className={`${INPUT_CLASS} w-full font-mono`}
              />
              <label className="inline-flex items-center gap-1 text-xs text-gray-600">
                <input
                  type="checkbox"
                  checked={r.secret}
                  onChange={(e) => update(i, { secret: e.target.checked })}
                />
                secret
              </label>
              {existing.has(r.key.trim()) && (
                <p className="col-span-3 -mt-1 text-xs text-amber-700">
                  {r.key.trim()} is already set; this replaces it.
                </p>
              )}
            </div>
          ))}
        </div>
        <Button
          size="sm"
          variant="ghost"
          className="mt-1"
          onClick={() => setRows((all) => [...all, { key: '', value: '', secret: true }])}
        >
          Add another
        </Button>
      </div>
    </FormDialog>
  );
};

/** Names only. Values are never sent to the console. */
const EnvNames: React.FC<{ env: { key: string; secret: boolean }[] }> = ({ env }) => (
  <div className="flex flex-wrap gap-1">
    {env.map((e) => (
      <span
        key={e.key}
        className={`rounded px-1.5 py-0.5 font-mono text-[11px] ${
          e.secret ? 'bg-amber-50 text-amber-800' : 'bg-gray-100 text-gray-700'
        }`}
        title={e.secret ? 'Encrypted at the provider' : 'Plain value at the provider'}
      >
        {e.key}
      </span>
    ))}
  </div>
);

const LogsCard: React.FC<{ app: CloudApp; pinned: { deploymentId: string } | null }> = ({
  app,
  pinned,
}) => {
  const [component, setComponent] = useState(app.components[0]?.name ?? '');
  const [type, setType] = useState<LogType>(pinned ? 'build' : 'run');
  const [lines, setLines] = useState(200);
  const q = useCloudLogsQuery({
    appId: app.id,
    component,
    type,
    lines,
    deploymentId: type === 'run' ? undefined : pinned?.deploymentId,
  });
  return (
    <Card
      title="Logs"
      note={
        pinned && type !== 'run'
          ? `deployment ${pinned.deploymentId.slice(0, 8)}`
          : 'masked for credentials; treat as sensitive'
      }
      action={
        <div className="flex items-center gap-2">
          <select
            value={component}
            onChange={(e) => setComponent(e.target.value)}
            aria-label="Component"
            className={`${INPUT_CLASS} h-8`}
          >
            {app.components.map((c) => (
              <option key={c.name} value={c.name}>
                {c.name}
              </option>
            ))}
          </select>
          <Tabs value={type} onChange={setType} options={LOG_TYPES} />
          <select
            value={lines}
            onChange={(e) => setLines(Number(e.target.value))}
            aria-label="Lines"
            className={`${INPUT_CLASS} h-8`}
          >
            {[100, 200, 500, 1000, 2000].map((n) => (
              <option key={n} value={n}>
                {n} lines
              </option>
            ))}
          </select>
          <Button size="sm" variant="outline" onClick={() => q.refetch()} aria-label="Reload logs">
            <RefreshCw className={`h-3.5 w-3.5 ${q.isFetching ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      }
      flush
    >
      {q.isError ? (
        <div className="p-4">
          <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
        </div>
      ) : q.isLoading ? (
        <Skeleton className="m-4 h-40" />
      ) : !q.data?.available || !q.data.text ? (
        <p className="p-8 text-center text-sm text-gray-400">No log lines for this selection.</p>
      ) : (
        <pre className="max-h-[32rem] overflow-auto whitespace-pre-wrap break-all bg-gray-950 p-4 font-mono text-xs leading-relaxed text-gray-100">
          {q.data.text}
        </pre>
      )}
    </Card>
  );
};

const CloudAppPage: React.FC = () => {
  const { id = '' } = useParams<{ id: string }>();
  const viewPerm = useHasPermission('cloud.view');
  const logsPerm = useHasPermission('cloud.logs');
  const operatePerm = useHasPermission('cloud.operate');
  const managePerm = useHasPermission('cloud.manage');
  const canView = viewPerm.isSuperAdmin || viewPerm.hasPermission;
  const canLogs = logsPerm.isSuperAdmin || logsPerm.hasPermission;
  const canOperate = operatePerm.isSuperAdmin || operatePerm.hasPermission;
  const canManage = managePerm.isSuperAdmin || managePerm.hasPermission;

  const [window, setWindow] = useState<MetricWindow>('1h');
  const [action, setAction] = useState<Action | null>(null);
  const [envTarget, setEnvTarget] = useState<string | null>(null);
  // ?log=<deployment id> opens on that deployment's build log; #logs scrolls to the logs.
  const [params] = useSearchParams();
  const { hash } = useLocation();
  const linked = params.get('log');
  const [logPin, setLogPin] = useState<{ deploymentId: string } | null>(
    linked ? { deploymentId: linked } : null,
  );
  const q = useCloudAppQuery(id, window, canView);
  const app = q.data?.app;
  const ready = !!app;
  useEffect(() => {
    if (ready && (hash === '#logs' || linked)) {
      document.getElementById('logs')?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [ready, hash, linked]);
  useBreadcrumbs([
    { label: 'Cloud', href: CLOUD_BASE },
    { label: 'Resources', href: `${CLOUD_BASE}/resources` },
    { label: app?.name ?? 'App' },
  ]);

  if (viewPerm.isLoading || (q.isLoading && !app)) {
    return (
      <PageShell title="Cloud" description="">
        <LoadingNote>Reading this app, its metrics and its deployments.</LoadingNote>
        <Skeleton className="h-64 w-full" />
      </PageShell>
    );
  }
  if (!canView) {
    return (
      <PageShell title="Cloud" description="">
        <AccessDenied />
      </PageShell>
    );
  }
  if (!app) {
    return (
      <PageShell title="Cloud" description="">
        <ErrorNotice error={q.error} onRetry={() => q.refetch()} />
      </PageShell>
    );
  }

  const running = app.in_progress_deployment;
  const activeId = app.active_deployment?.id;
  return (
    <PageShell
      title={app.name}
      description={[app.project, app.region, app.tier].filter(Boolean).join(', ')}
      actions={
        <div className="flex flex-wrap items-center gap-2">
          <FetchingChip />
          <HealthPill level={app.health.level} />
          <EnvBadge env={app.environment} />
          {app.live_url && (
            <a
              href={app.live_url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
            >
              Open <ExternalLink className="h-3.5 w-3.5" />
            </a>
          )}
          {canOperate && (
            <>
              <Button size="sm" variant="outline" onClick={() => setAction({ kind: 'restart' })}>
                Restart
              </Button>
              <Button size="sm" onClick={() => setAction({ kind: 'deploy' })}>
                Deploy
              </Button>
            </>
          )}
        </div>
      }
    >
      {q.isError && <ErrorNotice error={q.error} onRetry={() => q.refetch()} />}

      {running && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-blue-100 bg-blue-50 p-3 text-sm text-blue-800">
          <span>
            Deployment in progress: {running.phase.toLowerCase().replace(/_/g, ' ')}, step{' '}
            {running.steps.done} of {running.steps.total}. {deploymentCause(running)}
          </span>
          {canOperate && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => setAction({ kind: 'cancel', deployment: running })}
            >
              Cancel
            </Button>
          )}
        </div>
      )}

      <Card title="Health">
        <div className="grid grid-cols-2 gap-4 lg:grid-cols-5">
          <Field label="Status">
            <HealthPill level={app.health.level} />
            <HealthReasons health={app.health} className="mt-1" />
            {app.health.level === 'green' && app.health.reasons.length === 0 && (
              <p className="mt-1 text-xs text-gray-500">Nothing to report in the last hour.</p>
            )}
          </Field>
          <Field label="Memory (peak instance)">
            <UsagePct pct={app.metrics?.memory_pct} />
          </Field>
          <Field label="CPU (peak instance)">
            <UsagePct pct={app.metrics?.cpu_pct} />
          </Field>
          <Field label="Restarts, last hour">{app.metrics?.restarts ?? '—'}</Field>
          <Field label="Live release">
            {app.active_deployment ? (
              <span title={fmtTime(app.active_deployment.created_at)}>
                {fmtAgo(app.active_deployment.created_at)}
                <span className="block text-xs text-gray-500">
                  {deploymentCause(app.active_deployment)}
                </span>
              </span>
            ) : (
              'none'
            )}
          </Field>
        </div>
        {(app.domains ?? []).length > 0 && (
          <div className="mt-4 flex flex-wrap gap-x-6 gap-y-1 border-t border-gray-100 pt-3 text-xs text-gray-600">
            {(app.domains ?? []).map((d) => (
              <span key={d.domain}>
                {d.domain}: {d.phase.toLowerCase()}
                {d.certificate_expires_at
                  ? `, certificate to ${new Date(d.certificate_expires_at * 1000).toLocaleDateString()}`
                  : ''}
              </span>
            ))}
          </div>
        )}
      </Card>

      <Card title="Components" flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>Component</Th>
                <Th>Size</Th>
                <Th>Instances</Th>
                <Th>Source</Th>
                <Th>Environment</Th>
                <Th>{null}</Th>
              </tr>
            </thead>
            <tbody>
              {app.components.map((c) => (
                <ComponentRow
                  key={`${c.name}:${c.instance_count}`}
                  c={c}
                  canOperate={canOperate}
                  canManage={canManage}
                  onAction={setAction}
                  onEditEnv={() => setEnvTarget(c.name)}
                />
              ))}
            </tbody>
          </table>
        </div>
        {(app.env.length > 0 || canManage) && (
          <div className="border-t border-gray-100 p-4">
            <p className="mb-2 text-xs font-medium text-gray-500">
              App-level variables (names only)
              {canManage && (
                <button
                  type="button"
                  className="ml-3 text-blue-600 hover:underline"
                  onClick={() => setEnvTarget('app')}
                >
                  Edit
                </button>
              )}
            </p>
            {app.env.length > 0 ? (
              <EnvNames env={app.env} />
            ) : (
              <p className="text-xs text-gray-400">None set at app level.</p>
            )}
          </div>
        )}
      </Card>

      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-gray-800">Metrics</h2>
        <Tabs value={window} onChange={setWindow} options={METRIC_WINDOWS} />
      </div>
      <div className="grid grid-cols-1 gap-4 xl:grid-cols-3">
        <Card title="Memory %" note="dashed line at 85%">
          <MetricChart series={q.data!.series.memory} percent spanHours={SPAN_HOURS[window]} />
        </Card>
        <Card title="CPU %">
          <MetricChart series={q.data!.series.cpu} percent spanHours={SPAN_HOURS[window]} />
        </Card>
        <Card title="Restarts" note="any spike is a restart">
          <MetricChart series={q.data!.series.restarts} spanHours={SPAN_HOURS[window]} />
        </Card>
      </div>

      <Card title="Deployments" note="latest 20" flush>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left text-xs text-gray-500">
                <Th>Outcome</Th>
                <Th>Change</Th>
                <Th>By</Th>
                <Th>Started</Th>
                <Th right>Took</Th>
                <Th>{null}</Th>
              </tr>
            </thead>
            <tbody>
              {q.data!.deployments.length === 0 && <EmptyRow cols={6}>No deployments yet</EmptyRow>}
              {q.data!.deployments.map((d) => (
                <tr key={d.id} className="border-b border-gray-50 align-top last:border-0">
                  <td className="px-4 py-2">
                    <PhaseBadge phase={d.phase} />
                    {d.error && (
                      <p className="mt-0.5 max-w-xs text-xs text-red-700">
                        {d.error.message ?? d.error.code} ({d.error.step})
                      </p>
                    )}
                  </td>
                  <td
                    className="max-w-md truncate px-4 py-2 text-gray-700"
                    title={deploymentCause(d)}
                  >
                    {deploymentCause(d)}
                  </td>
                  <td className="px-4 py-2 text-gray-600">{d.by ?? '—'}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-gray-600">
                    {fmtTime(d.created_at)}
                  </td>
                  <td className="px-4 py-2 text-right text-gray-600">{fmtDur(d.duration)}</td>
                  <td className="whitespace-nowrap px-4 py-2 text-right">
                    {canLogs && (
                      <button
                        type="button"
                        className="mr-3 text-xs text-blue-600 hover:underline"
                        onClick={() => {
                          setLogPin({ deploymentId: d.id });
                          document.getElementById('logs')?.scrollIntoView({ behavior: 'smooth' });
                        }}
                      >
                        Build log
                      </button>
                    )}
                    {canOperate && isDeploying(d.phase) && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAction({ kind: 'cancel', deployment: d })}
                      >
                        Cancel
                      </Button>
                    )}
                    {canManage && d.phase === 'SUPERSEDED' && d.id !== activeId && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setAction({ kind: 'rollback', deployment: d })}
                      >
                        Roll back to this
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div id="logs" className="scroll-mt-4">
        {canLogs ? (
          // Remount on a new pin so the selectors start on that deployment's build log.
          <LogsCard key={logPin?.deploymentId ?? 'live'} app={app} pinned={logPin} />
        ) : (
          <AccessDenied code="cloud.logs" />
        )}
      </div>

      <AuditCard
        rows={q.data!.audit}
        empty="Nothing has been done to this app from the console"
      />

      {action && <ActionDialog app={app} action={action} onClose={() => setAction(null)} />}
      {envTarget && (
        <EnvDialog
          app={app}
          target={envTarget}
          env={
            envTarget === 'app'
              ? app.env
              : (app.components.find((c) => c.name === envTarget)?.env ?? [])
          }
          onClose={() => setEnvTarget(null)}
        />
      )}
    </PageShell>
  );
};

export default CloudAppPage;
